import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import type { AccountOverrideEntryRequest } from "@common/schemas/domains/accounting/account-override.schema";
import type { AccountOverrideEntry } from "@common/types/transaction";

interface LedgerLike {
    id: string;
    basis: AccountingLedgerBasis;
    isPrimary?: boolean;
}

/**
 * Picks the entity's primary ledger of the given basis from a list of
 * ledgers (typically the response from `useLedgers(entityId)`). Returns
 * `undefined` if no primary ledger of that basis exists.
 */
export function findPrimaryLedgerId(
    ledgers: LedgerLike[] | undefined,
    basis: AccountingLedgerBasis,
): string | undefined {
    if (!ledgers) {
        return undefined;
    }
    const primary = ledgers.find(
        (ledger) => ledger.basis === basis && ledger.isPrimary === true,
    );
    return primary?.id;
}

/**
 * Ledger the invoice line income account is stored on. Accrual books win when
 * the entity has a primary accrual ledger; otherwise the primary cash ledger,
 * so a cash-only entity can still code the line.
 */
export function findIncomeAccountLedgerId(
    ledgers: LedgerLike[] | undefined,
): string | undefined {
    return (
        findPrimaryLedgerId(ledgers, AccountingLedgerBasis.accruals) ??
        findPrimaryLedgerId(ledgers, AccountingLedgerBasis.cash)
    );
}

/**
 * Builds a single-entry override array for the entity's primary cash /
 * accrual ledger. Pass `accountId: null` to delete the existing
 * override on that ledger. Returns an empty array if `ledgerId` is
 * missing so callers can short-circuit a no-op write.
 */
export function buildPrimaryLedgerOverride(
    ledgerId: string | undefined,
    accountId: string | null,
): AccountOverrideEntryRequest[] {
    return buildLedgerOverride(ledgerId, accountId);
}

export function buildLedgerOverride(
    ledgerId: string | undefined,
    accountId: string | null,
): AccountOverrideEntryRequest[] {
    if (!ledgerId) {
        return [];
    }
    return [{ accountingLedgerId: ledgerId, accountId }];
}

/**
 * Reads the cash / accrual account assignment from the override array
 * for the supplied primary ledger ids. Returns `null` if no override
 * exists for that ledger.
 */
export function readOverrideAccountId(
    overrides: AccountOverrideEntry[] | undefined,
    ledgerId: string | undefined,
): string | null {
    if (!overrides || !ledgerId) {
        return null;
    }
    const entry = overrides.find((o) => o.accountingLedgerId === ledgerId);
    return entry?.accountId ?? null;
}
